import React, { forwardRef, useMemo, useState } from "react";
import { AutocompleteInputChangeReason, Box, createFilterOptions } from "@mui/material";
import { WorkflowActiveUser } from "./useActiveUsers";

/**
 * Search for the Builder's person pickers over the active-users list (~11,000 people on Dev).
 * Drawing every one of them made the dropdown lag behind the typing and show a stale, unrelated
 * list (2026-10-21). Now it draws a page at a time: the first 50 on open, the next 50 each time
 * the list is scrolled to the bottom - and the same for the matches once something is typed.
 */
export const WORKFLOW_USER_PAGE_SIZE = 50;

export const WORKFLOW_USER_NO_MATCH = "No matching users";
export const WORKFLOW_USER_MORE_HINT = "Scroll for more – or type to search";

/** How close to the bottom (px) counts as "reached the end" and draws the next page. */
const LOAD_MORE_THRESHOLD = 48;

const matchUsers = createFilterOptions<WorkflowActiveUser>({
  stringify: (user) => `${user.userName} ${user.email ?? ""}`,
});

/** Name, with the email under it - several people share a name. */
export const renderWorkflowUserOption = (
  { key: labelKey, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
  user: WorkflowActiveUser,
) => (
  <Box
    component="li"
    {...props}
    // MUI keys an option by its label, which repeats for people who share a name.
    key={`${user.userId}-${String(labelKey)}`}
    sx={{ flexDirection: "column", alignItems: "flex-start !important" }}
  >
    <span>{user.userName}</span>
    {user.email && (
      <Box component="span" sx={{ color: "text.secondary", fontSize: 12 }}>
        {user.email}
      </Box>
    )}
  </Box>
);

type UserListboxProps = React.HTMLAttributes<HTMLUListElement> & { "data-has-more"?: boolean };

/**
 * The dropdown list plus, while more people are left to draw, a last row saying so. Declared once
 * at module level (not per render) so the list keeps its scroll position as pages are added.
 */
const WorkflowUserListbox = forwardRef<HTMLUListElement, UserListboxProps>(
  ({ children, "data-has-more": hasMore, ...props }, ref) => (
    <ul {...props} ref={ref}>
      {children}
      {hasMore && (
        <Box
          component="li"
          role="presentation"
          className="workflow-user-search__more"
          sx={{ px: 2, py: 1, color: "text.secondary", fontSize: 12, fontStyle: "italic" }}
        >
          {WORKFLOW_USER_MORE_HINT}
        </Box>
      )}
    </ul>
  ),
);
WorkflowUserListbox.displayName = "WorkflowUserListbox";

/**
 * The search state one picker needs, as props to spread onto its Autocomplete. `searching` tells a
 * real search apart from the chosen person's name sitting in a single picker's box: opening the
 * list on a chosen person shows everyone (from the top), not just that one name.
 */
export const useWorkflowUserSearch = (users: WorkflowActiveUser[]) => {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [limit, setLimit] = useState(WORKFLOW_USER_PAGE_SIZE);

  const effectiveQuery = searching ? query.trim() : "";
  const matches = useMemo(
    () => (effectiveQuery ? matchUsers(users, { inputValue: effectiveQuery, getOptionLabel: (user) => user.userName }) : users),
    [users, effectiveQuery],
  );
  const shown = useMemo(() => matches.slice(0, limit), [matches, limit]);
  const hasMore = matches.length > shown.length;

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const list = event.currentTarget;
    // One page per redraw: several scroll events land at the bottom before the new rows are drawn,
    // and each must not add another page.
    if (hasMore && list.scrollTop + list.clientHeight >= list.scrollHeight - LOAD_MORE_THRESHOLD)
      setLimit((current) => (current === limit ? current + WORKFLOW_USER_PAGE_SIZE : current));
  };

  return {
    // The options are already narrowed and paged above; MUI's own filter would undo the paging.
    filterOptions: () => shown,
    renderOption: renderWorkflowUserOption,
    noOptionsText: WORKFLOW_USER_NO_MATCH,
    onInputChange: (_event: React.SyntheticEvent, next: string, reason: AutocompleteInputChangeReason) => {
      setQuery(next);
      setSearching(reason === "input");
      setLimit(WORKFLOW_USER_PAGE_SIZE);
    },
    // Reset on close, not open: typing into a closed picker fires the input change before MUI opens it.
    onClose: () => {
      setSearching(false);
      setLimit(WORKFLOW_USER_PAGE_SIZE);
    },
    ListboxComponent: WorkflowUserListbox,
    ListboxProps: { onScroll: handleScroll, "data-has-more": hasMore } as UserListboxProps,
  };
};
